import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Grid, Typography, alpha, Link } from '@mui/material';
import { motion } from 'framer-motion';
import { CommentOutlined, FormOutlined, DesktopOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { PageUrl } from 'const/page-url';
import { SECTION_PY } from 'const/section';

// ==============================|| INTRODUCTION FLOW SECTION ||============================== //
//
// SupportSection の直後に置く、問い合わせから契約までの4ステップ。
// 「無料トライアル」と言われても何が起きるのか分からないと申し込みづらいため、
// 申込の前後に何があるのか（システム説明 → 申込 → デモ → 判断）を先に開示している。
// 4番目を「導入のご判断」で終わらせているのは、試したら契約という流れではないことを示すため。

const primaryColor = '#2aa1af';

type Step = {
    icon: ReactNode;
    title: ReactNode;
    description: ReactNode;
};

const steps: Step[] = [
    {
        icon: <CommentOutlined />,
        title: 'システム説明',
        description: (
            <>
                サービスの概要や機能を
                <Box component="br" />
                担当者がご説明します。
            </>
        )
    },
    {
        icon: <FormOutlined />,
        title: (
            <>
                無料トライアル
                <Box component="br" />
                お申込み
            </>
        ),
        description: (
            <>
                <Link component={RouterLink} to={PageUrl.DocumentRequest.urlWithSlash} sx={{ color: primaryColor, fontWeight: 800 }}>
                    資料請求フォーム
                </Link>
                から
                <Box component="br" />
                お申し込みください。
            </>
        )
    },
    {
        icon: <DesktopOutlined />,
        title: (
            <>
                操作画面の
                <Box component="br" />
                デモ実施
            </>
        ),
        description: (
            <>
                担当者が画面を見ながら
                <Box component="br" />
                操作方法をご説明します。
            </>
        )
    },
    {
        icon: <CheckCircleOutlined />,
        title: '導入のご判断',
        description: (
            <>
                無料トライアルを行った上で、
                <Box component="br" />
                正式なお申込みをご案内します。
            </>
        )
    }
];

const IntroductionFlowSection = () => (
    <Box component="section" id="flow" sx={{ py: SECTION_PY, bgcolor: '#f4f7f7', overflow: 'hidden' }}>
        <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                >
                    <Typography
                        variant="caption"
                        component="span"
                        sx={{ color: primaryColor, fontWeight: 900, letterSpacing: '0.2em', display: 'block', mb: 2 }}
                    >
                        FLOW
                    </Typography>
                    <Typography
                        variant="h2"
                        component="h2"
                        sx={{ fontWeight: 900, fontSize: { xs: '1.9rem', md: '2.6rem' }, color: '#1a1a1a', lineHeight: 1.4 }}
                    >
                        導入までの流れ
                    </Typography>
                    <Box aria-hidden="true" sx={{ width: 56, height: 5, borderRadius: 3, bgcolor: primaryColor, mx: 'auto', mt: 2.5 }} />
                </motion.div>
            </Box>

            <Grid container spacing={3} alignItems="stretch">
                {steps.map((step, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.25 + index * 0.1 }}
                            style={{ height: '100%' }}
                        >
                            <Box
                                component="article"
                                sx={{
                                    height: '100%',
                                    p: { xs: 3, md: 3 },
                                    borderRadius: '20px',
                                    bgcolor: '#fff',
                                    border: `1px solid ${alpha(primaryColor, 0.15)}`,
                                    boxShadow: `0 10px 28px ${alpha(primaryColor, 0.05)}`,
                                    textAlign: 'center'
                                }}
                            >
                                {/* STEP 表記の上に置く小さなアイコン。内容を一目で見分けるための目印 */}
                                <Box
                                    aria-hidden="true"
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        mx: 'auto',
                                        mb: 1.5,
                                        borderRadius: '14px',
                                        bgcolor: alpha(primaryColor, 0.09),
                                        color: primaryColor,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '1.2rem'
                                    }}
                                >
                                    {step.icon}
                                </Box>
                                <Typography
                                    variant="caption"
                                    component="span"
                                    sx={{
                                        color: alpha(primaryColor, 0.85),
                                        fontWeight: 900,
                                        letterSpacing: '0.14em',
                                        display: 'block',
                                        mb: 1
                                    }}
                                >
                                    STEP {String(index + 1).padStart(2, '0')}
                                </Typography>
                                <Typography
                                    variant="h3"
                                    component="h3"
                                    sx={{
                                        fontWeight: 900,
                                        fontSize: { xs: '1.05rem', md: '1.12rem' },
                                        color: '#1a1a1a',
                                        lineHeight: 1.6,
                                        mb: 1.5
                                    }}
                                >
                                    {step.title}
                                </Typography>
                                <Typography component="p" sx={{ color: '#666', fontSize: '0.88rem', lineHeight: 1.9 }}>
                                    {step.description}
                                </Typography>
                            </Box>
                        </motion.div>
                    </Grid>
                ))}
            </Grid>
        </Container>
    </Box>
);

export default IntroductionFlowSection;
